import { useEffect, useState } from "react";

import { api, type Me } from "../lib/api.js";
import { controlCls, Field, FieldGroup } from "../ui/Field.js";

const SUPPORT_EMAIL = "support@koaform.com";

/**
 * Account settings.
 *
 * Name is editable; email is not. The address is both the account's identity
 * and its sign-in factor, so changing it needs a verify-the-new-address round
 * trip — until that exists, a text input here would silently lock people out
 * of their own forms. It routes to support instead, which is the same
 * contact-us model the paid features use.
 */
export const AccountDialog = ({
  me,
  onSaved,
  onClose,
}: {
  me: Me;
  onSaved: (me: Me) => void;
  onClose: () => void;
}) => {
  const [name, setName] = useState(me.name ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.updateMe(name.trim());
      onSaved(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Account settings"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="animate-fade fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-ink/45 p-6 backdrop-blur-[3px]"
    >
      <div className="animate-rise mt-[8vh] w-[460px] max-w-full rounded-[20px] bg-surface p-7 shadow-[0_30px_70px_-20px_rgba(12,26,46,0.5)]">
        <div className="mb-5 flex items-start justify-between gap-4">
          <h2 className="m-0 text-[21px] font-extrabold tracking-[-0.025em]">
            Account
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-[30px] w-[30px] flex-none rounded-lg bg-line text-[15px] text-muted hover:bg-line-input"
          >
            ×
          </button>
        </div>

        <Field
          label="Your name"
          hint="Shown on nothing public yet — it is how we address you in email."
        >
          <input
            className={controlCls}
            value={name}
            maxLength={120}
            placeholder="Jane Okafor"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <FieldGroup label="Email" className="mb-6">
          <p className="m-0 rounded-[10px] border border-line-input bg-subtle px-3.5 py-[11px] font-mono text-[13px] text-body">
            {me.email}
          </p>
          <p className="mt-1.5 mb-0 text-[12.5px] leading-[1.5] text-faint">
            This is how you sign in, so changing it needs a quick check that the
            new address works —{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold">
              email us
            </a>{" "}
            and we will move it.
          </p>
        </FieldGroup>

        {error ? (
          <p role="alert" className="mb-3 text-[13.5px] text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border border-line-bold bg-surface px-4 py-2.5 text-sm font-semibold text-ink hover:border-primary-600"
          >
            Close
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="rounded-[10px] bg-primary-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-700 disabled:bg-line-bold"
          >
            {busy ? "Saving…" : saved ? "Saved" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
